import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
    Activity,
    DollarSign,
    Clock,
    CheckCircle,
    RefreshCw,
    Terminal,
    CreditCard,
    Users,
    TrendingUp,
    FileText,
    UserPlus,
    Clock3,
    ArrowLeft,
    ArrowRight,
    X,
    Copy,
    Check,
    AlertCircle,
    Ban,
    Trash2,
    AlertTriangle
} from 'lucide-react';

interface DashboardStats {
    total: { count: number; amount: number };
    today: { count: number; amount: number };
    waiting: number;
    paid: number;
    processed: number;
}

interface Invoice {
    id: number;
    order_id: string;
    plan: string;
    amount: number;
    currency: string;
    status: string;
    username: string | null;
    created_at: string;
    expires_at: string;
    payment_url: string;
    processed: number;
}

interface PaymentLog {
    id: number;
    order_id: string;
    event_type: string;
    message: string;
    metadata: string;
    created_at: string;
}

const ITEMS_PER_PAGE = 10;

const InfoPanel: React.FC = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [logs, setLogs] = useState<PaymentLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState<string | null>(null); // Kept for future error handling
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [page, setPage] = useState(1);
    const [showConsole, setShowConsole] = useState(false);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const [clearing, setClearing] = useState(false);

    // Timer tick for countdown
    const [, setTimerTick] = useState(0);

    const loadDashboard = useCallback(async () => {
        try {
            const response = await fetch('/api/payments/admin/dashboard', { credentials: 'include' });
            const data = await response.json();
            if (data.success) setStats(data.stats);
        } catch (err) { console.error(err); }
    }, []);

    const loadInvoices = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.append('status', statusFilter);
            params.append('limit', ITEMS_PER_PAGE.toString());
            params.append('offset', ((page - 1) * ITEMS_PER_PAGE).toString());

            const response = await fetch(`/api/payments/admin/invoices?${params}`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) setInvoices(data.invoices);
        } catch (err) { console.error(err); }
    }, [statusFilter, page]);

    const loadLogs = useCallback(async () => {
        try {
            const response = await fetch('/api/payments/admin/logs?limit=50', { credentials: 'include' });
            const data = await response.json();
            if (data.success) setLogs(data.logs);
        } catch (err) { console.error(err); }
    }, []);

    const loadAllData = useCallback(async () => {
        // Only show spinner on first load or manual refresh, not auto-refresh
        if (!stats) setLoading(true);
        try {
            await Promise.all([loadDashboard(), loadInvoices(), loadLogs()]);
        } catch (err) {
            setError('Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    }, [loadDashboard, loadInvoices, loadLogs, stats]);

    useEffect(() => { loadAllData(); }, [loadAllData]);
    useEffect(() => { loadInvoices(); }, [page, statusFilter, loadInvoices]);

    useEffect(() => {
        if (!autoRefresh) return;
        const interval = setInterval(loadAllData, 10000);
        return () => clearInterval(interval);
    }, [autoRefresh, loadAllData]);

    useEffect(() => {
        const interval = setInterval(() => setTimerTick(t => t + 1), 1000);
        return () => clearInterval(interval);
    }, []);

    // Clear all data
    const handleClearData = async () => {
        setClearing(true);
        try {
            const res = await fetch('/api/payments/admin/invoices', { method: 'DELETE', credentials: 'include' });
            const data = await res.json();
            if (data.success) {
                setInvoices([]);
                setLogs([]);
                setStats(prev => prev ? ({ ...prev, total: { count: 0, amount: 0 }, today: { count: 0, amount: 0 }, waiting: 0, paid: 0, processed: 0 }) : null);
                setShowClearConfirm(false);
            } else {
                setError('Failed to clear data');
            }
        } catch (err) {
            setError('Error clearing data');
        } finally {
            setClearing(false);
        }
    };

    const handleCopy = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const getTimeRemaining = (expiresAt: string) => {
        const remaining = new Date(expiresAt).getTime() - Date.now();
        if (remaining <= 0) return { text: 'Expired', isExpired: true };
        const m = Math.floor(remaining / 60000);
        const s = Math.floor((remaining % 60000) / 1000);
        return { text: `${m}:${s.toString().padStart(2, '0')}`, isExpired: false };
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleString('ru-RU', {
            hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: '2-digit'
        });
    };

    const getStatusBadge = (status: string, expiresAt: string) => {
        const { isExpired } = getTimeRemaining(expiresAt);

        // If status is waiting but time expired, show Expired immediately
        if (status === 'waiting' && isExpired) {
            return (
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-500/10 text-red-400 border border-red-500/20 flex items-center gap-1.5 w-fit">
                    <Ban className="w-3 h-3" /> Expired
                </span>
            );
        }
        if (status === 'waiting') {
            return (
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 flex items-center gap-1.5 w-fit">
                    <Clock className="w-3 h-3" /> Waiting
                </span>
            );
        }
        if (status === 'paid') {
            return (
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-500/10 text-green-400 border border-green-500/20 flex items-center gap-1.5 w-fit">
                    <CheckCircle className="w-3 h-3" /> Paid
                </span>
            );
        }
        return (
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-500/10 text-gray-400 border border-gray-500/20 w-fit">
                {status}
            </span>
        );
    };

    const getLogIcon = (type: string) => {
        switch (type) {
            case 'invoice_created': return <FileText className="w-4 h-4 text-blue-400" />;
            case 'payment_received': return <DollarSign className="w-4 h-4 text-green-400" />;
            case 'account_created': return <UserPlus className="w-4 h-4 text-purple-400" />;
            case 'invoice_expired': return <AlertCircle className="w-4 h-4 text-red-400" />;
            default: return <Activity className="w-4 h-4 text-gray-400" />;
        }
    };

    if (user?.role !== 'admin') return <div className="p-8 text-center text-red-400">Access Denied</div>;

    return (
        <div className="space-y-8 animate-fade-in pb-10">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-1">Payment Center</h1>
                    <p className="text-white/40 text-sm">Real-time payment monitoring</p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowClearConfirm(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-xl text-red-400 transition-all hover:border-red-500/40"
                    >
                        <Trash2 className="w-4 h-4" />
                        <span>Clear</span>
                    </button>

                    <button
                        onClick={() => setShowConsole(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white transition-all hover:border-white/20"
                    >
                        <Terminal className="w-4 h-4" />
                        <span>Console</span>
                    </button>

                    <button
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        className={`relative flex items-center gap-2 px-4 py-2 rounded-xl border transition-all ${autoRefresh
                            ? 'bg-purple-500/10 border-purple-500/30 text-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.15)] hover:bg-purple-500/20'
                            : 'bg-transparent border-white/10 text-white/50 hover:text-white hover:bg-white/5'
                            }`}
                        disabled={loading && !stats}
                    >
                        <RefreshCw
                            className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                            style={autoRefresh && !loading ? { animation: 'spin 3s linear infinite' } : {}}
                        />
                        <span>{autoRefresh ? 'Auto-Update' : 'Paused'}</span>
                        {autoRefresh && <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />}
                    </button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {[
                    { label: 'Total Invoices', value: stats?.total.count || 0, sub: `$${stats?.total.amount?.toFixed(0) || '0'}`, icon: CreditCard, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
                    { label: 'Today', value: stats?.today.count || 0, sub: `$${stats?.today.amount?.toFixed(0) || '0'}`, icon: TrendingUp, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' },
                    { label: 'Waiting', value: stats?.waiting || 0, sub: 'Pending', icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
                    { label: 'Paid', value: stats?.paid || 0, sub: 'Success', icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
                    { label: 'Accounts', value: stats?.processed || 0, sub: 'Created', icon: Users, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' }
                ].map((item, i) => (
                    <div key={i} className={`p-4 rounded-xl border ${item.border} ${item.bg} backdrop-blur-sm transition-transform hover:-translate-y-1`}>
                        <div className="flex items-center justify-between mb-3">
                            <div className={`p-2 rounded-lg bg-white/5 ${item.color}`}>
                                <item.icon className="w-5 h-5" />
                            </div>
                        </div>
                        <div className="text-2xl font-bold text-white mb-1">{item.value}</div>
                        <div className="text-xs text-white/40">{item.label} • {item.sub}</div>
                    </div>
                ))}
            </div>

            {/* Invoices List */}
            <div className="rounded-xl bg-white/[0.02] border border-white/10 overflow-hidden">
                <div className="p-4 border-b border-white/10 flex items-center justify-between bg-white/[0.02]">
                    <div className="flex items-center gap-3">
                        <DollarSign className="w-5 h-5 text-green-400" />
                        <h2 className="text-lg font-bold text-white">Transactions</h2>
                    </div>
                    <div className="flex gap-2">
                        {['', 'waiting', 'paid'].map(s => (
                            <button
                                key={s}
                                onClick={() => setStatusFilter(s)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === s ? 'bg-white text-black' : 'text-white/40 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="overflow-x-auto min-h-[400px]">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-white/5 text-xs text-white/40 uppercase tracking-wider">
                                <th className="px-5 py-3 font-medium">Order</th>
                                <th className="px-5 py-3 font-medium">Plan/Amount</th>
                                <th className="px-5 py-3 font-medium">User</th>
                                <th className="px-5 py-3 font-medium">Status & Timer</th>
                                <th className="px-5 py-3 font-medium text-right">Time</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm divide-y divide-white/5">
                            {invoices.map(invoice => {
                                const { text: timerText, isExpired } = getTimeRemaining(invoice.expires_at);
                                return (
                                    <tr key={invoice.id} className="hover:bg-white/[0.02] transition-colors group">
                                        <td className="px-5 py-4">
                                            <button
                                                onClick={() => handleCopy(invoice.order_id, invoice.order_id)}
                                                className="flex items-center gap-2 font-mono text-white/70 hover:text-purple-400 transition-colors group/btn text-left"
                                                title="Click to copy full Order ID"
                                            >
                                                <span>
                                                    {invoice.order_id.length > 20
                                                        ? `...${invoice.order_id.slice(-12)}`
                                                        : invoice.order_id}
                                                </span>
                                                {copiedId === invoice.order_id ? (
                                                    <Check className="w-3 h-3 text-green-400" />
                                                ) : (
                                                    <Copy className="w-3 h-3 opacity-0 group-hover/btn:opacity-100 transition-opacity" />
                                                )}
                                            </button>
                                        </td>
                                        <td className="px-5 py-4">
                                            <div className="flex flex-col">
                                                <span className="font-semibold text-white capitalize">{invoice.plan}</span>
                                                <span className="text-xs text-white/40">${invoice.amount} USDT</span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-4">
                                            {invoice.username ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-xs font-bold">
                                                        {invoice.username[0].toUpperCase()}
                                                    </div>
                                                    <span className="text-white/80">{invoice.username}</span>
                                                </div>
                                            ) : (
                                                <span className="text-white/20">—</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-4">
                                            <div className="flex items-center gap-3">
                                                {getStatusBadge(invoice.status, invoice.expires_at)}
                                                {invoice.status === 'waiting' && !isExpired && (
                                                    <div className="flex items-center gap-1.5 text-xs font-mono text-yellow-500/80 bg-yellow-500/5 px-2 py-1 rounded">
                                                        <Clock3 className="w-3 h-3" />
                                                        {timerText}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-5 py-4 text-right text-white/30 text-xs">
                                            {formatDate(invoice.created_at)}
                                        </td>
                                    </tr>
                                );
                            })}
                            {invoices.length === 0 && !loading && (
                                <tr><td colSpan={5} className="py-20 text-center text-white/20">No invoices found</td></tr>
                            )}
                            {loading && invoices.length === 0 && (
                                <tr><td colSpan={5} className="py-20 text-center text-white/20">Loading data...</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="p-4 border-t border-white/10 flex items-center justify-between bg-white/[0.02]">
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent text-white transition-colors text-sm"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Prev
                    </button>
                    <span className="text-sm text-white/40 font-mono">Page {page}</span>
                    <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={invoices.length < ITEMS_PER_PAGE}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent text-white transition-colors text-sm"
                    >
                        Next
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Console Modal */}
            {
                showConsole && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in" onClick={() => setShowConsole(false)}>
                        <div
                            className="bg-[#0a0a0a] border border-white/10 rounded-xl w-full max-w-4xl h-[80vh] shadow-2xl flex flex-col animate-scale-in"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-4 border-b border-white/10 flex items-center justify-between bg-white/[0.02]">
                                <div className="flex items-center gap-3">
                                    <Terminal className="w-5 h-5 text-green-400" />
                                    <h2 className="text-lg font-bold text-white">System Events</h2>
                                    <span className="px-2 py-0.5 rounded-full bg-white/10 text-xs text-white/50">{logs.length} events</span>
                                </div>
                                <button onClick={() => setShowConsole(false)} className="p-2 hover:bg-white/10 rounded-lg text-white/50 hover:text-white transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 font-mono text-sm space-y-2 custom-scrollbar">
                                {logs.map((log) => (
                                    <div key={log.id} className="group relative pl-4 border-l border-white/10 hover:border-purple-500/50 transition-colors py-2">
                                        <div className="absolute left-[-5px] top-3 w-2.5 h-2.5 rounded-full bg-[#0a0a0a] border border-white/20 group-hover:border-purple-500 transition-colors" />
                                        <div className="flex items-start gap-4">
                                            <div className="min-w-[140px] text-xs text-white/30 pt-0.5">
                                                {formatDate(log.created_at)}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    {getLogIcon(log.event_type)}
                                                    <span className={`font-semibold text-xs uppercase tracking-wider ${log.event_type === 'payment_received' ? 'text-green-400' :
                                                        log.event_type === 'invoice_created' ? 'text-blue-400' :
                                                            log.event_type === 'account_created' ? 'text-purple-400' : 'text-gray-400'
                                                        }`}>
                                                        {log.event_type.replace('_', ' ')}
                                                    </span>
                                                    {log.order_id && (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/30 font-mono">
                                                            #{log.order_id.slice(-6)}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-white/70 text-sm leading-relaxed">{log.message}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {logs.length === 0 && (
                                    <div className="h-full flex flex-col items-center justify-center text-white/20 gap-2">
                                        <Terminal className="w-8 h-8 opacity-50" />
                                        <p>No system logs available</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }
            {/* Clear Confirmation Modal */}
            {
                showClearConfirm && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in" onClick={() => setShowClearConfirm(false)}>
                        <div
                            className="bg-[#0a0a0a] border border-red-500/30 rounded-xl w-full max-w-md p-6 shadow-2xl animate-scale-in"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                                    <AlertTriangle className="w-6 h-6 text-red-500" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">Clear All Data?</h3>
                                    <p className="text-white/50 text-sm">This action cannot be undone.</p>
                                </div>
                            </div>

                            <p className="text-white/70 mb-6 text-sm leading-relaxed">
                                This will permanently delete <span className="text-white font-bold">all invoices</span> and <span className="text-white font-bold">logs</span> from the database.
                                Current active payments will be lost.
                            </p>

                            <div className="flex items-center gap-3 justify-end">
                                <button
                                    onClick={() => setShowClearConfirm(false)}
                                    className="px-4 py-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleClearData}
                                    disabled={clearing}
                                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors shadow-lg shadow-red-900/20"
                                >
                                    {clearing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                    {clearing ? 'Clearing...' : 'Confirm Clear'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default InfoPanel;
